// `useFilters` — sole writer of dashboard URL search params (FILT-03).
//
// Plan 05-05 contract:
//   - Reads `useSearchParams()` from react-router v7
//   - Returns `{ filters, setFilters, activeChips }`
//   - On first mount with bare URL: writes DEFAULT_FILTERS via replace:true (FILT-02)
//   - Slider drag (radius-only changes): URL writes debounced 300ms with replace:true (FILT-03)
//   - Other filter changes: write immediately with default push (history entry)
//   - validates / clamps untrusted URL input via parseFilters (T-05-14 mitigation)
//
// Pitfall 6: never mutates `searchParams` directly — always goes through
// `setSearchParams` so React Router rerenders. The slider's local-state
// instant-feedback layer lives inside `<RadiusSlider>`; this hook only
// owns the URL-persistence layer.
//
// Phase 08 (Plan 08-02): the `highlight` field was added to FilterState. It
// is a non-radius field, so it falls under the existing immediate-write-with-
// push branch via diffFilterKeys. No structural change to useFilters required.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import type { FilterState } from '@/types/db';
import {
  DASHBOARD_DEFAULTS,
  diffFilterKeys,
  filtersEqual,
  parseFilters,
  serializeFilters,
} from '@/lib/filterDefaults';
import { computeActiveChips, type ActiveFilterChipSpec } from '@/lib/activeChips';

const SLIDER_DEBOUNCE_MS = 300;

export interface UseFiltersResult {
  /** Current filter state, validated against URL. */
  filters: FilterState;
  /**
   * Update the filter state. Accepts either a full FilterState (the contract
   * exposed by `<FilterBar>`) or a partial patch (used by chip × removal).
   *
   * Behavior:
   * - If only `radius` changed → URL write debounced 300ms with replace:true
   * - Otherwise → URL write immediate with push (new history entry)
   * - No-op writes (filtersEqual) are skipped to keep history clean
   */
  setFilters: (next: Partial<FilterState> | FilterState) => void;
  /** Chips representing each non-default filter (FILT-05). */
  activeChips: ActiveFilterChipSpec[];
}

export function useFilters(
  pageDefaults: FilterState = DASHBOARD_DEFAULTS,
): UseFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => parseFilters(searchParams, pageDefaults),
    [searchParams, pageDefaults],
  );

  // ---------------------------------------------------------------------
  // First-mount default seeding (FILT-02). Bare URL → write defaults via replace.
  // We only run this once. Subsequent renders observe seeded params and skip.
  // ---------------------------------------------------------------------
  const didSeedRef = useRef<boolean>(false);
  useEffect(() => {
    if (didSeedRef.current) return;
    didSeedRef.current = true;
    if (Array.from(searchParams.keys()).length === 0) {
      setSearchParams(serializeFilters(pageDefaults), { replace: true });
    }
    // intentionally empty deps: this fires once on mount; React Strict Mode's
    // double-invoke is guarded by didSeedRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------
  // Debounced slider write
  // ---------------------------------------------------------------------
  const debounceTimerRef = useRef<number | null>(null);
  const pendingSliderRef = useRef<FilterState | null>(null);

  // Cleanup on unmount: never fire a debounced write on a torn-down hook.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current != null) {
        window.clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingSliderRef.current = null;
    };
  }, []);

  const cancelPendingSlider = useCallback(() => {
    if (debounceTimerRef.current != null) {
      window.clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingSliderRef.current = null;
  }, []);

  const setFilters = useCallback(
    (patchOrFull: Partial<FilterState> | FilterState) => {
      // Normalize input: accept either full state (FilterBar) or partial (chip remove).
      const next: FilterState = { ...filters, ...patchOrFull };
      if (filtersEqual(filters, next)) return;

      const changedKeys = diffFilterKeys(filters, next);
      const isSliderOnly = changedKeys.length === 1 && changedKeys[0] === 'radius';

      if (isSliderOnly) {
        pendingSliderRef.current = next;
        if (debounceTimerRef.current != null) {
          window.clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = window.setTimeout(() => {
          const queued = pendingSliderRef.current;
          pendingSliderRef.current = null;
          debounceTimerRef.current = null;
          if (queued) {
            // replace:true so slider drags don't litter the back-button history
            setSearchParams(serializeFilters(queued), { replace: true });
          }
        }, SLIDER_DEBOUNCE_MS);
      } else {
        // Cancel any in-flight slider debounce so the immediate write isn't
        // overwritten by a stale debounced write that fires later.
        cancelPendingSlider();
        setSearchParams(serializeFilters(next));
      }
    },
    [filters, setSearchParams, cancelPendingSlider],
  );

  const activeChips = useMemo<ActiveFilterChipSpec[]>(
    () => computeActiveChips(filters),
    [filters],
  );

  return { filters, setFilters, activeChips };
}
